import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../context/AuthProvider"; // Ensure path is correct
import toast from "react-hot-toast";
import { TbUserEdit, TbDeviceFloppy, TbX } from "react-icons/tb"; // Added more icons
import { FiMail, FiPhone, FiUser, FiImage, FiKey } from "react-icons/fi"; // Icons for form fields

const UserProfile = () => {
  const { user, setUser } = useContext(AuthContext);
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/"; // Fallback

  // Initialize state with values from user context, providing fallbacks
  const [updatedName, setUpdatedName] = useState("");
  const [updatedPhone, setUpdatedPhone] = useState("");
  const [updatedPhoto, setUpdatedPhoto] = useState("");
  
  // Effect to update local state when user context changes (e.g., after login or initial load)
  useEffect(() => {
    if (user) {
      setUpdatedName(user.fullname || "Unknown User");
      setUpdatedPhone(user.phone || ""); // Keep empty if not available, placeholder will handle it
      setUpdatedPhoto(user.photo || "https://placehold.co/150x150/E9ECEF/333?text=No+Image"); // Default placeholder
    }
  }, [user]);


  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [, setMessage] = useState(""); // setMessage was not used, can be removed if not needed for other logic

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(""); // Not used

    // Basic validation (optional, can be enhanced)
    if (!updatedName.trim()) {
        toast.error("Full name cannot be empty.");
        setLoading(false);
        return;
    }
    // Basic URL validation for photo (optional)
    if (updatedPhoto && !updatedPhoto.match(/^https?:\/\/.+\..+/)) {
        toast.error("Please enter a valid image URL (starting with http/https).");
        setLoading(false);
        return;
    }


    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found. Please log in again.");
        setLoading(false);
        return;
      }

      const res = await fetch(`${baseUrl}auth/update`, { // Ensure this endpoint is correct
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fullname: updatedName,
          phone: updatedPhone,
          photo: updatedPhoto,
        }),
      });

      const data = await res.json();

      if (data.success && data.user) { // Check if data.user exists
        setUser(data.user); // Update context
        toast.success("Profile updated successfully!");
        // setMessage("Profile updated successfully!"); // Not used
        setShowForm(false);
      } else {
        toast.error(data.message || "Failed to update profile. Please try again.");
      }
    } catch (err) {
      console.error("Profile update error:", err);
      toast.error("Something went wrong. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = "#0f4372"; // Kept for reference, but new design uses gradient more
  const gradientColor = "linear-gradient(to right, #e5272e, #f59332)";
  const accentColor = "#e5272e"; // Red from gradient for accents

  // Fallback for user details if user object is null or undefined initially
  const displayFullname = user?.fullname || "Unknown User";
  const displayEmail = user?.email || "Not Available";
  const displayUserId = user?._id || "N/A";
  const displayPhone = user?.phone; // Will be handled by conditional rendering
  const displayPhoto = user?.photo || "https://placehold.co/150x150/E9ECEF/333?text=No+Image";

  return (
    <>
    <style>
        {`
        .user-profile-card {
            max-width: 650px; /* Slightly wider card */
            border-radius: 16px; /* More rounded */
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1); /* Softer shadow */
            border: none; /* Remove default card border */
        }
        .user-profile-header {
            background-image: ${gradientColor};
            color: white;
            border-top-left-radius: 16px;
            border-top-right-radius: 16px;
            padding: 1.5rem; /* More padding */
        }
        .user-profile-header h4 {
            font-weight: 600; /* Bolder header title */
        }
        .profile-image-wrapper {
            margin-top: -60px; /* Pull image up */
            margin-bottom: 1rem;
            position: relative;
            display: inline-block; /* To keep border tight */
        }
        .profile-image {
            width: 130px;
            height: 130px;
            object-fit: cover;
            border-radius: 50%;
            border: 5px solid white; /* White border to separate from gradient */
            box-shadow: 0 4px 15px rgba(0,0,0,0.15);
        }
        .user-profile-card .card-title {
            color: ${accentColor}; /* Use accent color for name */
            font-weight: 700;
            font-size: 1.75rem; /* Larger name */
        }
        .user-profile-card .card-text {
            font-size: 1rem;
            color: #555;
        }
        .user-profile-card .user-id-text {
            font-size: 0.85rem;
            color: #777;
        }
        .btn-profile-action {
            padding: 0.6rem 1.2rem;
            border-radius: 25px; /* Pill shape */
            font-weight: 600;
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 180px; /* Ensure buttons have good width */
        }
        .btn-profile-action svg {
            margin-right: 8px;
        }
        .btn-update-profile {
            background-color: ${accentColor};
            border-color: ${accentColor};
            color: white;
        }
        .btn-update-profile:hover {
            background-color: #c02027; /* Darker red */
            border-color: #c02027;
            color: white;
            transform: translateY(-2px);
        }
        .btn-cancel-update {
            background-color: transparent;
            border-color: #6c757d; /* Gray border */
            color: #6c757d;
        }
        .btn-cancel-update:hover {
            background-color: #6c757d;
            border-color: #6c757d;
            color: white;
        }
        .profile-form-section {
            padding: 1.5rem 2rem; /* Padding for form section */
        }
        .profile-form-section .form-label {
            font-weight: 500;
            color: #495057;
            font-size: 0.9rem;
        }
        .profile-form-section .form-control {
            border-radius: 8px;
            padding: 0.75rem 1rem;
            border: 1px solid #ced4da;
        }
        .profile-form-section .form-control:focus {
            border-color: ${accentColor};
            box-shadow: 0 0 0 0.2rem rgba(229, 39, 46, 0.2);
        }
        .btn-save-profile {
            background-image: ${gradientColor};
            color: white;
            border: none;
            font-size: 1rem;
        }
        .btn-save-profile:hover {
            opacity: 0.9;
            color: white;
        }
        .info-icon {
            margin-right: 8px;
            color: #777;
        }
        `}
    </style>
    <div className="container d-flex justify-content-center align-items-start py-4"> {/* align-items-start for better layout if form grows */}
      <div className="card shadow-lg user-profile-card w-100"> {/* Added shadow-lg */}
        <div className="card-header user-profile-header text-center">
          <h4 className="m-0">My Profile</h4>
        </div>

        <div className="card-body text-center pt-0"> {/* pt-0 to manage space with pulled-up image */}
          <div className="profile-image-wrapper">
            <img
              src={showForm ? updatedPhoto : displayPhoto} // Show updatedPhoto if form is open for preview
              alt="Profile"
              className="profile-image"
              onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x150/E9ECEF/333?text=Invalid+URL"; }}
            />
          </div>
          <h5 className="card-title mt-2">{showForm ? updatedName : displayFullname}</h5>
          <p className="card-text text-muted mb-1">
            <FiMail className="info-icon" /> {displayEmail}
          </p>
          {(showForm ? updatedPhone : displayPhone) && ( // Show phone if available in form or display
            <p className="card-text text-muted mb-1">
              <FiPhone className="info-icon" /> {showForm ? updatedPhone : displayPhone}
            </p>
          )}
          <p className="card-text user-id-text mt-2">
            User ID: {displayUserId}
          </p>
          <button
            className={`btn btn-profile-action mt-3 ${showForm ? 'btn-cancel-update' : 'btn-update-profile'}`}
            onClick={() => {
              setShowForm((prev) => !prev);
              if (!showForm && user) { // Reset form fields to current user data when opening
                setUpdatedName(user.fullname || "Unknown User");
                setUpdatedPhone(user.phone || "");
                setUpdatedPhoto(user.photo || "https://placehold.co/150x150/E9ECEF/333?text=No+Image");
              }
            }}
          >
            {showForm ? <><TbX /> Cancel</> : <><TbUserEdit /> Update Profile</>}
          </button>
        </div>

        {showForm && (
          <div className="card-body border-top profile-form-section">
            <h5 className="mb-3 text-center" style={{color: accentColor, fontWeight: 600}}>Edit Your Profile</h5>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="profileFullName" className="form-label"><FiUser className="info-icon"/>Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="profileFullName"
                  value={updatedName}
                  onChange={(e) => setUpdatedName(e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="profilePhotoUrl" className="form-label"><FiImage className="info-icon"/>Image URL</label>
                <input
                  type="text"
                  className="form-control"
                  id="profilePhotoUrl"
                  value={updatedPhoto}
                  onChange={(e) => setUpdatedPhoto(e.target.value)}
                  placeholder="https://example.com/your-image.jpg"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="profilePhoneNumber" className="form-label"><FiPhone className="info-icon"/>Phone Number</label>
                <input
                  type="tel" // Changed to tel for better semantics
                  className="form-control"
                  id="profilePhoneNumber"
                  value={updatedPhone}
                  onChange={(e) => setUpdatedPhone(e.target.value)}
                  placeholder="Enter your phone number (optional)"
                />
              </div>
              <button
                type="submit"
                className="btn btn-profile-action btn-save-profile w-100 mt-2"
                disabled={loading}
              >
                {loading ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                    </>
                ) : (
                    <><TbDeviceFloppy /> Save Changes</>
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default UserProfile;
